import { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  FormControl,
  Select,
  IconButton
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Logout,
  Person,
  AccountCircle
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#6B0F2A !important',
  background: '#6B0F2A !important',
  backdropFilter: 'blur(20px)',
  borderBottom: `1px solid rgba(255, 255, 255, 0.2)`,
  boxShadow: `0 8px 32px rgba(107, 15, 42, 0.3)`,
  position: 'relative',
  zIndex: theme.zIndex.drawer + 1,
  borderRadius: '0px !important',
}));

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function Header() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const open = Boolean(anchorEl);

  // Get username from localStorage (read each render — avoid stale empty-deps memo)
  let username = 'User';
  let isCdxUser = false;
  try {
    username = localStorage.getItem('username') || 'User';
    const cdxUser = localStorage.getItem('cdx_user');
    isCdxUser = cdxUser === 'true' || cdxUser === true;
  } catch (error) {
    console.error('Error reading user info from localStorage:', error);
  }

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}synctrack/tenants`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Sort tenants by name
      const sortedTenants = [...data].sort((a, b) => 
        (a.tenantName || '').localeCompare(b.tenantName || '')
      );
      
      setTenants(sortedTenants);
      
      // Get saved tenant from localStorage
      const savedTenantId = localStorage.getItem('tenantId');
      const savedTenant = localStorage.getItem('selectedTenant');
      
      if (savedTenantId && savedTenant) {
        if (savedTenantId === 'ALL' && savedTenant === 'ALL') {
          // Handle "All" selection
          setSelectedTenant('ALL');
          setSelectedTenantId(null);
        } else {
          // Handle specific tenant selection
          const tenant = sortedTenants.find(t => t.tenantId.toString() === savedTenantId);
          if (tenant) {
            setSelectedTenant(tenant.tenantName);
            setSelectedTenantId(tenant.tenantId);
          }
        }
      } else if (sortedTenants.length > 0) {
        // If no saved tenant, but we have a tenantId from login, find the matching tenant
        const currentTenantId = localStorage.getItem('tenantId');
        if (currentTenantId && currentTenantId !== 'null' && currentTenantId !== 'undefined') {
          // Find tenant that matches the current tenantId from login
          const matchingTenant = sortedTenants.find(t => t.tenantId.toString() === currentTenantId);
          if (matchingTenant) {
            setSelectedTenant(matchingTenant.tenantName);
            setSelectedTenantId(matchingTenant.tenantId);
            localStorage.setItem('selectedTenant', matchingTenant.tenantName);
          } else {
            // Fallback to first tenant only if no match found
            const firstTenant = sortedTenants[0];
            setSelectedTenant(firstTenant.tenantName);
            setSelectedTenantId(firstTenant.tenantId);
            localStorage.setItem('selectedTenant', firstTenant.tenantName);
            localStorage.setItem('tenantId', firstTenant.tenantId.toString());
          }
        } else {
          // Only if no tenantId at all, select the first one
          const firstTenant = sortedTenants[0];
          setSelectedTenant(firstTenant.tenantName);
          setSelectedTenantId(firstTenant.tenantId);
          localStorage.setItem('selectedTenant', firstTenant.tenantName);
          localStorage.setItem('tenantId', firstTenant.tenantId.toString());
        }
      }
    } catch (error) {
      console.error('Error fetching tenants:', error);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTenantChange = (event) => {
    const selectedTenantName = event.target.value;
    
    if (selectedTenantName === 'ALL') {
      // Handle "All" selection
      setSelectedTenant('ALL');
      setSelectedTenantId(null);
      
      // Update localStorage with "All" selection
      localStorage.setItem('selectedTenant', 'ALL');
      localStorage.setItem('tenantId', 'ALL');
      
      // Dispatch custom event for other components
      window.dispatchEvent(new CustomEvent('tenantChange', { 
        detail: { 
          tenantName: 'ALL', 
          tenantId: 'ALL' 
        } 
      }));
      
      // Refresh the page to ensure all data reflects the new tenant
      window.location.reload();
    } else {
      // Handle specific tenant selection
      const tenant = tenants.find(t => t.tenantName === selectedTenantName);
      
      if (tenant) {
        setSelectedTenant(tenant.tenantName);
        setSelectedTenantId(tenant.tenantId);
        
        // Update localStorage with both tenant name and ID
        localStorage.setItem('selectedTenant', tenant.tenantName);
        localStorage.setItem('tenantId', tenant.tenantId.toString());
        
        // Dispatch custom event for other components with both name and ID
        window.dispatchEvent(new CustomEvent('tenantChange', { 
          detail: { 
            tenantName: tenant.tenantName, 
            tenantId: tenant.tenantId 
          } 
        }));
        
        // Refresh the page to ensure all data reflects the new tenant
        window.location.reload();
      }
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleProfileNavigation = () => {
    handleClose();
    navigate('/profile');
  };

  const handleDashboardNavigation = () => {
    handleClose();
    if (isCdxUser) {
      navigate('/dashboard');
    } else {
      navigate('/sync-flow');
    }
  };

  const handleLogout = async () => {
    try {
      // Retrieve the token from localStorage or sessionStorage
      const token = localStorage.getItem('token') || sessionStorage.getItem('sessionToken');
      
      if (!token) {
        console.error('No token found');
        // Clear storage and navigate to login even if token is missing
        localStorage.clear();
        sessionStorage.clear();
        handleClose();
        navigate('/');
        return;
      }

      // Auth server relays session audit logout to Integration BackEnd (JWT verified there with jjwt 0.2)
      const sessionAuditId = localStorage.getItem('sessionAuditId');
      const response = await fetch(`${API_BASE_URL}auth/logout`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(sessionAuditId ? { sessionAuditId } : {}),
      });

      if (!response.ok) {
        throw new Error(`Logout failed! status: ${response.status}`);
      }

      // On successful logout, clear storage
      localStorage.clear();
      sessionStorage.clear();
      
      // Close the menu and redirect to login page
      handleClose();
      navigate('/');
    } catch (error) {
      console.error('Error during logout:', error);
      localStorage.removeItem('sessionAuditId');
      localStorage.clear();
      sessionStorage.clear();
      handleClose();
      navigate('/');
    }
  };

  return (
    <StyledAppBar sx={{ backgroundColor: '#6B0F2A !important' }}>
      <Toolbar variant="dense" sx={{ justifyContent: "space-between", px: 3, borderRadius: '0px !important' }}>
        {/* Left Section - Title */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: '#FFFFFF',
            }}
          >
            AsroSync
          </Typography>
        </Box>

        {/* Right Section - User Info */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isCdxUser && (
            <FormControl size="small" sx={{ minWidth: 120, m: 0 }}>
              <Select
                value={selectedTenant}
                onChange={handleTenantChange}
                displayEmpty
                sx={{
                  height: 32,
                  backgroundColor: 'transparent',
                  borderRadius: '8px',
                  '& .MuiSelect-select': {
                    py: 0.5,
                    px: 1.5,
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.3)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.5)',
                  }
                }}
              >
                <MenuItem value="" disabled>Select Tenant</MenuItem>
                <MenuItem value="ALL">All</MenuItem>
                {tenants.map((tenant) => (
                  <MenuItem key={tenant.tenantId} value={tenant.tenantName}>
                    {tenant.tenantName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
         
          <Typography variant="body2" sx={{ lineHeight: 1, color: '#FFFFFF' }}>
            {username}
          </Typography>

          <IconButton
            onClick={handleClick}
            size="small"
            sx={{
              p: 0.5,
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            <AccountCircle sx={{ width: 32, height: 32, color: '#FFFFFF' }} />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            PaperProps={{
              elevation: 8,
              sx: {
                overflow: 'visible',
                mt: 1.5,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '12px',
                border: '1px solid rgba(184, 216, 216, 0.3)',
                boxShadow: '0 8px 32px rgba(79, 99, 103, 0.2)',
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem onClick={handleProfileNavigation}>
              <ListItemIcon>
                <Person fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Profile</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" color="primary" />
              </ListItemIcon>
              <ListItemText>Logout</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </StyledAppBar>
  );
}